import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { reloadOncePerBuild, reloadPage } from './chunk-reload';

/**
 * Lazy-loaded route chunks are hash-named (`chunk-<hash>.js`). A deploy replaces
 * them with new hashes, so a tab opened before the deploy fails to fetch the old
 * chunk on its next lazy navigation — surfacing as "Failed to fetch dynamically
 * imported module" / "Importing a module script failed" / a "not a valid
 * JavaScript MIME type" error (the SPA index served where the missing JS was
 * expected). One full reload pulls the fresh index.html + current chunks and the
 * navigation succeeds. Non-chunk errors delegate to Sentry's handler unchanged.
 *
 * Fixes the post-deploy stale-chunk noise: Sentry ANGULAR-FRONTEND-3 / -4 / -8.
 *
 * Epic 86 Story 86.19 (self pass P1/B1): a `@defer` block without an `@error` block whose dependencies fail to load is
 * the same failure one level down, but Angular drops the import's own error and reports `RuntimeError(-750)` instead,
 * whose production message is exactly "NG0750" (the dev message starts "NG0750: Loading dependencies for `@defer` block
 * failed"). Angular keeps the block failed for the page's life, so the phone Artists hub (one deferred chunk) would stay
 * blank until a reload: the same reload recovers it.
 *
 * 86.19 (Codex gate F2): the reload is budgeted, not rate-limited. A 30 s debounce let a failure that persists reload
 * again whenever it arrived more than 30 s after the last (a stalled connection or proxy), without end. Now each build
 * spends at most one automatic reload (`chunk-reload.ts`, shared with the surfaces that draw their own load error); once
 * it is spent nothing reloads and the error goes to Sentry.
 */
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|not a valid JavaScript MIME type/i;
/** Angular's `RuntimeErrorCode.DEFER_LOADING_FAILED` (-750) and its message's code, in production and in dev. */
const DEFER_LOADING_FAILED = -750;
const DEFER_LOADING_FAILED_MESSAGE = /^NG0?750\b/;

@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private readonly sentryHandler = Sentry.createErrorHandler({ showDialog: false });

  handleError(error: unknown): void {
    const message =
      (error as { message?: string } | null)?.message ?? String(error ?? '');

    const deferFailed = (error as { code?: unknown } | null)?.code === DEFER_LOADING_FAILED || DEFER_LOADING_FAILED_MESSAGE.test(message);

    // Browser-only (SSR has no window/sessionStorage and no lazy-chunk fetch). A recovered failure is not reported; one
    // the build's budget can no longer recover is.
    if ((CHUNK_LOAD_ERROR.test(message) || deferFailed) && reloadOncePerBuild(() => this.reload())) return;

    this.sentryHandler.handleError(error);
  }

  /** The full reload (a seam: jsdom's `location` cannot be redefined). */
  protected reload(): void {
    reloadPage();
  }
}
